import {
  Box,
  FormControl,
  MenuItem,
  MenuList,
  Popover,
  Skeleton,
  Typography
} from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import React, { useRef, useState } from 'react'
import LinkIcon from '../../../../../assets/icons/link.svg?component'
import CalendarIcon from '../../../../../assets/icons/calendar.svg?component'
import { DayDataTypes } from '../../../../../api/models/dataList.model'
import { RestrictionTypeList } from '../../../../../api/models/restrictionTypeList.model'
import { useTranslation } from 'react-i18next'
import { HtmlTooltip } from 'src/components/htmlTooltip/HTMLTooltip'
import { TooltipChildren } from 'src/components/tooltipChildren/TooltipChildren'
import { ArrowDropDown } from '@mui/icons-material'
import {
  RatesDataGridRow,
  RowType
} from '../contexts/ratesDataGrid/RatesDataGridContext.types'
import { useRatesDataGridContext } from '../contexts/ratesDataGrid/RatesDataGridContext'

interface Props {
  cellParams: GridRenderCellParams
  openCreateRatePriceModal: (row: any) => void
  restrictionTypeList: RestrictionTypeList[]
}

const RatesGridCol2CellRender: ReactFC<Props> = ({
  cellParams: { row, id, value },
  openCreateRatePriceModal,
  restrictionTypeList
}) => {
  const { changeRateRowDataType } = useRatesDataGridContext()
  const anchorEl = useRef()
  const rowData = row as RatesDataGridRow & { index: number }
  const { t } = useTranslation()
  const [openDropdown, setOpenDropdown] = useState(false)
  const [showMore, setShowMore] = useState(false)
  if (rowData.type.includes('Skeleton')) {
    return <Skeleton variant='rectangular' width='100%' height='60%' />
  }
  if (
    rowData.type !== RowType.RATE &&
    rowData.type !== RowType.CHANNEL &&
    rowData.type !== RowType.CAPACITY
  ) {
    return value ?? null
  }

  const handleClose = () => {
    setOpenDropdown(false)
    setShowMore(false)
  }

  const handleChangeDataType = (val: keyof DayDataTypes) => {
    changeRateRowDataType({
      row,
      dataType: val,
      index: row.index
    })
    handleClose()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between'
      }}
    >
      <FormControl
        sx={{
          width: '80%'
        }}
      >
        <Box
          sx={{
            cursor: 'pointer',
            display: 'flex'
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setOpenDropdown(true)
          }}
          ref={anchorEl}
        >
          <Typography
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              wordWrap: 'break-word',
              whiteSpace: 'nowrap'
            }}
          >
            {restrictionTypeList.find(
              (restriction) => restriction.id === rowData.dayDataType
            )?.name || t('labels.Prices')}
          </Typography>
          <ArrowDropDown />
        </Box>
        <Popover
          anchorEl={anchorEl.current}
          open={openDropdown}
          onClose={() => handleClose()}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          <MenuList>
            <MenuItem
              value='price'
              onClick={() => {
                handleChangeDataType('price')
              }}
            >
              {t('labels.Prices')}
            </MenuItem>
            {(showMore
              ? restrictionTypeList
              : restrictionTypeList.slice(0, 3)
            )?.map((restriction) => (
              <MenuItem
                key={`${id}-${restriction.id}`}
                onClick={() => {
                  handleChangeDataType(restriction.id)
                }}
              >
                {restriction.name}
              </MenuItem>
            ))}
            {!showMore && (
              <MenuItem
                onClick={() => {
                  setShowMore(true)
                }}
              >
                Mostrar más
              </MenuItem>
            )}
          </MenuList>
        </Popover>
      </FormControl>
      <Box
        onClick={() => {
          if (rowData.inherited) return
          openCreateRatePriceModal(row)
        }}
        style={{
          width: '20%',
          height: '1rem',
          marginRight: '.5rem',
          cursor: rowData.inherited ? 'default' : 'pointer'
        }}
      >
        {rowData.inherited ? (
          <HtmlTooltip
            title={
              <ul>
                {rowData.inheritedDataList?.map((inheritedData, index) => (
                  <li key={`${rowData.id}-inheritedData-${index}`}>
                    {inheritedData}
                  </li>
                ))}
              </ul>
            }
          >
            <TooltipChildren>
              <LinkIcon />
            </TooltipChildren>
          </HtmlTooltip>
        ) : (
          <CalendarIcon />
        )}
      </Box>
    </Box>
  )
}
export default React.memo(RatesGridCol2CellRender)
